// src/pages/Partner.jsx
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronRight, 
  Check, 
//   Star, 
  DollarSign, 
  Users, 
  Clock, 
  Globe,
//   Percent,
//   Award,
  Calendar,
  ShieldCheck,
  PieChart,
  ArrowRight,
//   Image,
  BarChart2,
  TrendingUp,
  Phone,
  Mail,
  ChevronDown,
  MessageSquare
} from 'lucide-react';
import '../styles/partner.css';

const Partner = () => {
  const [activeTab, setActiveTab] = useState('individuals');
  const [activeFaq, setActiveFaq] = useState(null);
  const videoRef = useRef(null);
  
  const plans = [
    {
      id: 'essential',
      name: 'Essential',
      price: '$49',
      period: 'per month',
      description: 'Perfect for new restaurants looking to build their online presence',
      features: [
        'Basic profile listing',
        'Halal certification display',
        'Online reservations',
        'Menu management',
        'Customer reviews',
        'Up to 10 photos',
        'Mobile optimization'
      ],
      missing: [
        'Priority search placement',
        'Featured restaurant status',
        'Premium analytics',
        'Marketing tools'
      ],
      cta: 'Start Free Trial',
      popular: false
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '$99',
      period: 'per month',
      description: 'Full suite of tools for established restaurants to maximize bookings',
      features: [
        'Everything in Essential',
        'Priority search placement',
        'Featured restaurant status',
        'Advanced analytics',
        'Menu translation',
        'Customer database & insights',
        'Unlimited photos',
        'Special offers management',
        'Email marketing tools'
      ],
      missing: [],
      cta: 'Get Started',
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 'Custom',
      period: 'tailored package',
      description: 'For restaurant groups and chains with multiple locations',
      features: [
        'Everything in Professional',
        'Multiple location management',
        'Brand controls',
        'API access',
        'Dedicated account manager',
        'Custom integrations',
        'Advanced reporting',
        'Priority support 24/7',
        'Staff training'
      ],
      missing: [],
      cta: 'Contact Sales',
      popular: false
    }
  ];
  
  const benefits = [
    {
      id: 1,
      title: 'Targeted Visibility',
      description: 'Connect directly with diners specifically searching for halal dining options',
      icon: <Users size={24} />
    },
    {
      id: 2,
      title: 'Increased Bookings',
      description: 'Our partners see an average 87% increase in reservations within 3 months',
      icon: <Calendar size={24} />
    },
    {
      id: 3,
      title: 'Halal Certification Highlight',
      description: 'Clearly display your certification status and build trust with customers',
      icon: <ShieldCheck size={24} />
    },
    {
      id: 4,
      title: 'Real-Time Analytics',
      description: 'Access detailed insights about your customers and reservation patterns',
      icon: <PieChart size={24} />
    },
    {
      id: 5,
      title: 'Seamless Reservation System',
      description: 'Eliminate no-shows with our integrated booking and confirmation system',
      icon: <Clock size={24} />
    },
    {
      id: 6,
      title: 'Global Reach',
      description: 'Attract international travelers searching for authentic halal experiences',
      icon: <Globe size={24} />
    }
  ];
  
  const testimonials = [
    {
      id: 1,
      quote: "Since joining the platform, we've seen a 135% increase in bookings from customers specifically looking for halal options. The detailed analytics have helped us optimize our menu offerings.",
      author: "Mohammed Ahmed",
      role: "Owner, Al-Madina Grill",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1974",
      stars: 5
    },
    {
      id: 2,
      quote: "The platform took care of all our online presence needs. Our restaurant is now fully booked every weekend, and we've expanded our dining space to accommodate the increased demand.",
      author: "Fatima Khan",
      role: "Manager, Saffron House",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=1974",
      stars: 5
    },
    {
      id: 3,
      quote: "As newcomers to the city, we were worried about building a customer base. Within months of joining, we had regular customers and strong word-of-mouth referrals through the platform.",
      author: "Ibrahim Khalid",
      role: "Chef & Owner, Levant Fusion",
      image: "https://images.unsplash.com/photo-1566492031773-4f4e44671857?q=80&w=1974",
      stars: 4
    }
  ];
  
  const faqs = [
    {
      id: 1,
      question: "What are the requirements to join as a partner restaurant?",
      answer: "To join our platform, restaurants must provide halal food options (either fully halal certified or with clearly marked halal menu items). We also require basic business information, high-quality photos of your restaurant and dishes, and a complete menu listing. Our team will guide you through the verification process."
    },
    {
      id: 2,
      question: "How long does it take to get my restaurant listed?",
      answer: "Once you've submitted all required information and photos, our team typically completes the verification process within 3-5 business days. After approval, your restaurant will be live on our platform, and you'll have immediate access to the partner dashboard."
    },
    {
      id: 3,
      question: "What fees or commissions do you charge?",
      answer: "We operate on a subscription model rather than charging per-reservation commissions. This means you pay a flat monthly fee based on your chosen plan, with no additional charges per booking or customer. We offer three tiers of plans to suit different restaurant sizes and needs."
    },
    {
      id: 4,
      question: "Do I need to have a halal certification to join?",
      answer: "While having an official halal certification is preferred, we also welcome restaurants that offer halal menu options without formal certification. In this case, you'll need to clearly indicate which menu items are halal, and our platform will display your restaurant as 'Offers Halal Options' rather than 'Halal Certified'."
    },
    {
      id: 5,
      question: "How does the reservation system work?",
      answer: "Our reservation system integrates directly with your existing setup. Customers book through our platform, and you receive notifications via email, SMS, or directly in your management dashboard. You can confirm or suggest alternative times, and all bookings are automatically synced to prevent double-bookings."
    },
    {
      id: 6,
      question: "Can I offer special promotions to platform users?",
      answer: "Absolutely! Our Professional and Enterprise plans include special offer management tools that allow you to create and promote exclusive deals, happy hours, or special events to our users. These promotions are highlighted in search results and can significantly increase your visibility."
    }
  ];
  
  const handleVideoPlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
  };
  
  const toggleFaq = (id) => {
    if (activeFaq === id) {
      setActiveFaq(null);
    } else {
      setActiveFaq(id);
    }
  };
  
  return (
    <div className="partner-page">
      {/* Hero Section */}
      <div className="partner-hero">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <span className="hero-badge">Join Our Platform</span>
              <h1 className="hero-title">Grow Your Restaurant With The Largest Halal Dining Platform</h1>
              <p className="hero-subtitle">Connect with thousands of diners specifically searching for authentic halal dining experiences</p>
              <div className="hero-stats">
                <div className="hero-stat-item">
                  <div className="stat-value">500+</div>
                  <div className="stat-label">Cities</div>
                </div>
                <div className="hero-stat-item">
                  <div className="stat-value">2.5M+</div>
                  <div className="stat-label">Monthly Users</div>
                </div>
                <div className="hero-stat-item">
                  <div className="stat-value">87%</div>
                  <div className="stat-label">Booking Increase</div>
                </div>
              </div>
              <div className="hero-buttons">
                <Link to="/register-restaurant" className="btn btn-primary btn-lg">
                  Become a Partner <ArrowRight size={18} />
                </Link>
                <button 
                  className="btn btn-outline-light btn-lg video-btn"
                  onClick={handleVideoPlay}
                >
                  Watch How It Works
                </button>
              </div>
            </div>
            <div className="col-lg-6 d-none d-lg-block">
              <div className="hero-image-container">
                <div className="floating-card stat-card">
                  <BarChart2 size={20} />
                  <span>Bookings up 42% this month</span>
                </div>
                <div className="floating-card review-card">
                  <div className="review-stars">★★★★★</div>
                  <p>"Best halal restaurant finder!"</p>
                </div>
                <img 
                  src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2074" 
                  alt="Restaurant Partner" 
                  className="hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Benefits Section */}
      <div className="benefits-section">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-badge">Platform Benefits</span>
            <h2 className="section-title">Why Partner With Us?</h2>
            <p className="section-subtitle">Join thousands of successful restaurant partners who have transformed their business</p>
          </div>
          
          <div className="row g-4 mt-5">
            {benefits.map(benefit => (
              <div key={benefit.id} className="col-md-6 col-lg-4">
                <div className="benefit-card">
                  <div className="benefit-icon">
                    {benefit.icon}
                  </div>
                  <h3 className="benefit-title">{benefit.title}</h3>
                  <p className="benefit-description">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* How It Works Section */}
      <div className="how-it-works-section">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-badge">Simple Process</span>
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">Get started in just a few simple steps</p>
          </div>
          
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Register Your Restaurant</h3>
                <p>Fill out our simple application form with your restaurant details and halal offerings.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Complete Your Profile</h3>
                <p>Upload high-quality photos, add your menu, and highlight your halal certification.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Verification Process</h3>
                <p>Our team reviews your information to ensure quality standards for our platform.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Go Live & Get Bookings</h3>
                <p>Once approved, your restaurant appears in searches and starts receiving bookings.</p>
              </div>
            </div>
          </div>
          
          <div className="video-container">
            <video
              ref={videoRef}
              poster="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070"
              controls
            >
              <source src="#" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <div className="video-overlay" onClick={handleVideoPlay}>
              <div className="play-button">
                <svg viewBox="0 0 24 24" width="64" height="64" stroke="currentColor" strokeWidth="2" fill="none">
                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Plans Section */}
      <div className="plans-section">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-badge">Flexible Options</span>
            <h2 className="section-title">Choose the Right Plan</h2>
            <p className="section-subtitle">Select a plan that matches your restaurant's needs</p>
          </div>
          
          <div className="plan-tabs">
            <button 
              className={`plan-tab ${activeTab === 'individuals' ? 'active' : ''}`}
              onClick={() => setActiveTab('individuals')}
            >
              Individual Restaurants
            </button>
            <button 
              className={`plan-tab ${activeTab === 'chains' ? 'active' : ''}`}
              onClick={() => setActiveTab('chains')}
            >
              Restaurant Chains
            </button>
          </div>
          
          <div className="row g-4 mt-4">
            {plans.map(plan => (
              <div key={plan.id} className="col-md-6 col-lg-4">
                <div className={`plan-card ${plan.popular ? 'popular' : ''}`}>
                  {plan.popular && <div className="popular-badge">Most Popular</div>}
                  <div className="plan-header">
                    <h3 className="plan-name">{plan.name}</h3>
                    <div className="plan-price">
                      <span className="price">{plan.price}</span>
                      <span className="period">{plan.period}</span>
                    </div>
                  </div>
                  <div className="plan-description">
                    {plan.description}
                  </div>
                  <div className="plan-features">
                    <h4>What's included:</h4>
                    <ul className="feature-list">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="feature-included">
                          <Check size={16} />
                          <span>{feature}</span>
                        </li>
                      ))}
                      {plan.missing.map((feature, index) => (
                        <li key={index} className="feature-missing">
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="plan-footer">
                    <Link 
                      to={plan.id === 'enterprise' ? '/contact-sales' : '/register-restaurant'}
                      className={`btn ${plan.popular ? 'btn-primary' : 'btn-outline-primary'} w-100`}
                    >
                      {plan.cta}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="plan-notice text-center mt-4">
            <p>All plans include 24/7 support and a 14-day free trial. No credit card required to start.</p>
          </div>
        </div>
      </div>
      
      {/* Success Stories Section */}
      <div className="success-stories-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-5">
              <div className="success-content">
                <span className="section-badge">Success Stories</span>
                <h2 className="section-title">Our Partners' Results</h2>
                <p className="mb-4">Hear from restaurant owners who transformed their business by joining our platform.</p>
                
                <div className="success-metrics">
                  <div className="metric">
                    <TrendingUp size={20} />
                    <div className="metric-text">
                      <span className="metric-value">87%</span>
                      <span className="metric-label">Average increase in monthly bookings</span>
                    </div>
                  </div>
                  <div className="metric">
                    <Users size={20} />
                    <div className="metric-text">
                      <span className="metric-value">35%</span>
                      <span className="metric-label">Higher customer retention rate</span>
                    </div>
                  </div>
                  <div className="metric">
                    <DollarSign size={20} />
                    <div className="metric-text">
                      <span className="metric-value">42%</span>
                      <span className="metric-label">Increase in average revenue</span>
                    </div>
                  </div>
                </div>
                
                <Link to="/success-stories" className="btn btn-outline-primary mt-4">
                  Read All Success Stories <ChevronRight size={16} />
                </Link>
              </div>
            </div>
            <div className="col-lg-7">
              <div className="testimonials-carousel">
                {testimonials.map(testimonial => (
                  <div key={testimonial.id} className="testimonial-card">
                    <div className="testimonial-stars">
                      {'★'.repeat(testimonial.stars)}
                      {'☆'.repeat(5 - testimonial.stars)}
                    </div>
                    <blockquote>"{testimonial.quote}"</blockquote>
                    <div className="testimonial-author">
                      <img src={testimonial.image} alt={testimonial.author} />
                      <div className="author-info">
                        <div className="author-name">{testimonial.author}</div>
                        <div className="author-role">{testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* FAQ Section */}
      <div className="faq-section">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-badge">Common Questions</span>
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">Find answers to common questions about partnering with us</p>
          </div>
          
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="faq-list">
                {faqs.map(faq => (
                  <div 
                    key={faq.id} 
                    className={`faq-item ${activeFaq === faq.id ? 'active' : ''}`}
                    onClick={() => toggleFaq(faq.id)}
                  >
                    <div className="faq-question">
                      <h3>{faq.question}</h3>
                      <ChevronDown 
                        size={20} 
                        className={`faq-icon ${activeFaq === faq.id ? 'rotated' : ''}`}
                      />
                    </div>
                    <div className={`faq-answer ${activeFaq === faq.id ? 'open' : ''}`}>
                      <p>{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="faq-more text-center mt-5">
                <p>Still have questions? We're here to help!</p>
                <div className="faq-contact-options">
                  <a href="tel:+18001234567" className="btn btn-outline-primary me-3">
                    <Phone size={18} className="me-2" /> Call Us
                  </a>
                  <Link to="/contact" className="btn btn-outline-primary">
                    <Mail size={18} className="me-2" /> Email Support
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Join Banner */}
      <div className="join-banner">
        <div className="banner-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
        <div className="container">
          <div className="join-card">
            <div className="row align-items-center">
              <div className="col-lg-7">
                <h2 className="mb-3">Ready to Grow Your Restaurant Business?</h2>
                <p className="mb-4">Join thousands of successful partners who have transformed their restaurants with our platform</p>
                <div className="banner-features">
                  <div className="feature-item">
                    <Check size={20} />
                    <span>Free 14-day trial</span>
                  </div>
                  <div className="feature-item">
                    <Check size={20} />
                    <span>No setup fees</span>
                  </div>
                  <div className="feature-item">
                    <Check size={20} />
                    <span>Cancel anytime</span>
                  </div>
                </div>
                <div className="banner-actions">
                  <Link to="/register-restaurant" className="btn btn-primary btn-lg">
                    Register Your Restaurant
                  </Link>
                  <Link to="/partner-support" className="btn btn-outline-dark btn-lg">
                    <MessageSquare size={18} className="me-2" /> Talk to Sales
                  </Link>
                </div>
              </div>
              <div className="col-lg-5 d-none d-lg-block">
                <div className="join-image-wrapper">
                  <div className="join-image-inner">
                    <img 
                      src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2070" 
                      alt="Restaurant Partner"
                      className="join-image"
                    />
                    <div className="image-overlay"></div>
                    <div className="stats-overlay">
                      <div className="stat-item">
                        <div className="stat-number">500+</div>
                        <div className="stat-text">Cities</div>
                      </div>
                      <div className="stat-divider"></div>
                      <div className="stat-item">
                        <div className="stat-number">25k+</div>
                        <div className="stat-text">Restaurants</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Partner;